import { Link } from "react-router-dom";
import { Bot, Utensils, Globe, ArrowUpRight, Sparkles, Zap, PenTool, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import Reveal from "../components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1800&q=70";
const AI_IMG = "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1400&q=70";
const DESIGN_IMG = "https://images.unsplash.com/photo-1561070791-2526d30994b8?auto=format&fit=crop&w=1400&q=70";
const REST_IMG = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1400&q=70";

const PILLAR_ICONS = [PenTool, Utensils, Bot];

const Digital = () => {
  const { t } = useLang();
  const d = t.divisions.digitia;

  return (
    <div className="pt-20">
      {/* HERO */}
      <section className="relative pt-24 pb-24 sm:pt-32 sm:pb-32 overflow-hidden bg-[#0A192F] isolate">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Digital automation" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-[#0A192F]/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/40 via-[#0A192F]/60 to-[#0A192F]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <Reveal>
            <div className="inline-flex items-center gap-2 border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
              <Sparkles className="w-3 h-3" /> {t.common.secondary_division}
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
              {d.name}
              <span className="block italic text-[#D4AF37] text-3xl sm:text-4xl lg:text-5xl mt-3">"{d.tag}"</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-gray-200 leading-relaxed">{d.intro}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="digital-hero-cta"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-white transition-colors"
              >
                {d.cta_primary} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href="#pillars"
                data-testid="digital-hero-scroll"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-white hover:text-[#0A192F] transition-colors"
              >
                {d.cta_secondary}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section id="pillars" className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">{d.overline}</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] tracking-tight max-w-3xl">
              Three pillars that turn everyday businesses into <span className="italic text-[#D4AF37]">intelligent</span> ones.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
            {d.pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i] || Zap;
              return (
                <Reveal key={p.t} delay={i * 0.06}>
                  <div data-testid={`digital-pillar-${i}`} className="bg-white p-10 h-full group hover:bg-[#0A192F] hover:text-white transition-colors">
                    <Icon className="w-8 h-8 text-[#D4AF37]" />
                    <h3 className="mt-6 font-serif text-2xl text-[#0A192F] group-hover:text-white">{p.t}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-gray-600 group-hover:text-gray-300">{p.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI FEATURE STRIP */}
      <section className="bg-[#0A192F] text-white py-24 relative isolate">
        <div className="absolute inset-0 z-0">
          <img src={AI_IMG} alt="AI" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0A192F]/85" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">AI for Everyday Business</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
                Never miss an order, a message, or a booking again.
              </h3>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-gray-300 leading-relaxed max-w-xl">
                Our AI assistants live on your WhatsApp, Instagram DMs, website chat and phone line — replying instantly in Spanish, English or any language your customers speak. They take orders, book tables, quote prices and hand off to your team when it matters.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "24/7 replies on WhatsApp, IG DMs & website chat",
                  "Automated order-taking with menu & upsells",
                  "Table & appointment booking with reminders",
                  "Multilingual: Spanish, English, French, Arabic + more",
                  "CRM sync — every conversation becomes a lead",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-200">{line}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.2}>
              <div className="relative">
                <img src={REST_IMG} alt="Restaurant automation" className="w-full h-[440px] object-cover" loading="lazy" />
                <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] text-[#0A192F] px-6 py-4 max-w-xs">
                  <p className="text-[10px] uppercase tracking-[0.3em]">Case Study</p>
                  <p className="mt-1 font-serif text-lg leading-tight">+37% orders in 60 days for a Valencia bistro after AI ordering.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICE GRID */}
      <section className="bg-gray-100 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Reveal>
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Everything We Deliver</p>
              </Reveal>
              <Reveal delay={0.05}>
                <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-[#0A192F] tracking-tight max-w-2xl">
                  From idea to launch — one team, one invoice.
                </h3>
              </Reveal>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
            {d.items.map((it, i) => (
              <Reveal key={it} delay={(i % 8) * 0.03}>
                <div data-testid={`digital-service-${i}`} className="bg-white p-6 h-full hover:bg-[#0A192F] group transition-colors">
                  <Check className="w-5 h-5 text-[#D4AF37]" />
                  <h4 className="mt-4 font-serif text-lg text-[#0A192F] group-hover:text-white leading-snug">{it}</h4>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Who We Serve</p>
              </Reveal>
              <Reveal delay={0.05}>
                <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-[#0A192F] tracking-tight leading-tight">
                  Built for the businesses that keep Spain moving.
                </h3>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-gray-600 leading-relaxed">
                  Whether you run a neighbourhood tapas bar, a mobile phone shop, a boutique hotel or a growing delivery service — Digitia adapts to how you already work and layers modern design, tech and automation on top.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 relative overflow-hidden">
                  <img src={DESIGN_IMG} alt="Design work" className="w-full h-64 object-cover" loading="lazy" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
                {d.industries.map((ind, i) => (
                  <Reveal key={ind.k} delay={i * 0.04}>
                    <div data-testid={`digital-industry-${i}`} className="bg-white p-7 h-full hover:bg-[#0A192F] group transition-colors">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{ind.v}</p>
                      <p className="mt-3 font-serif text-xl text-[#0A192F] group-hover:text-white leading-tight">{ind.k}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F] py-24 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Globe className="w-10 h-10 mx-auto text-[#D4AF37]" />
          </Reveal>
          <Reveal delay={0.05}>
            <h3 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Let&apos;s turn your business into a <span className="italic text-[#D4AF37]">smart</span> one.
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Book a free 20-minute audit. We&apos;ll show you exactly which parts of your business can be automated this month — and what a new brand or website could look like.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="digital-final-cta"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-white transition-colors"
              >
                {d.cta_primary} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Digital;
